import type { FilterSchema } from '@/shared/components/filter/validation'
import type { RepricingMetricsModel } from '@/shared/generated'

import { any, isNil, last, prop } from 'lodash/fp'
import { ArrowDownRightIcon, ArrowUpRightIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useRepricingMetricsStatisticsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import {
	getFinishedAndLockedOptimizations,
	useOptimizationsQuery,
} from '@/entities/optimization'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetEmptyState from '../../WidgetEmptyState'
import WidgetErrorState from '../../WidgetErrorState'

export default function NumericAveragePriceChangeWidget({
	commonFilters = [],
}: {
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()
	const currency = useSelectedCurrency()

	const optimizationsQuery = useOptimizationsQuery({
		select: getFinishedAndLockedOptimizations,
	})
	const repricingMetricsStatisticsQuery = useRepricingMetricsStatisticsQuery(
		{
			optimizations: optimizationsQuery.data || [],
			filters: commonFilters,
			currency,
		},
		{
			enabled: !!optimizationsQuery.data?.length,
			select: (data: RepricingMetricsModel[] | null) =>
				last(data)?.average_price_change_percent,
		}
	)
	const isLoading = any(prop('isLoading'), [
		optimizationsQuery,
		repricingMetricsStatisticsQuery,
	])
	const isError = any(prop('isError'), [
		optimizationsQuery,
		repricingMetricsStatisticsQuery,
	])
	const averagePriceChangePercent = repricingMetricsStatisticsQuery?.data

	return (
		<Widget className='relative flex flex-auto flex-col'>
			<WidgetHeader
				title={t('widget.avg_price_change.title')}
				subtitle={t('widget.avg_price_change.subtitle', {
					defaultValue: 'widget.avg_price_change.subtitle',
				})}
				tooltip={t('widget.avg_price_change.tooltip', {
					defaultValue: 'widget.avg_price_change.tooltip',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='runcate pt-2'>
				{isLoading && <Skeleton className='col-span-2 h-10 w-32' />}
				{isNil(averagePriceChangePercent) && !isLoading && !isError && (
					<WidgetEmptyState size='small' />
				)}
				{!isLoading && !isNil(averagePriceChangePercent) && (
					<div className='flex w-full items-center'>
						<span className='data-text'>
							{formatNumber(averagePriceChangePercent, NumberFormats.Percent)}
						</span>
						{averagePriceChangePercent > 0 && (
							<ArrowUpRightIcon className='text-success' />
						)}
						{averagePriceChangePercent < 0 && (
							<ArrowDownRightIcon className='text-destructive' />
						)}
					</div>
				)}
			</WidgetContent>
		</Widget>
	)
}
